import { useFeatureValue } from '@shared/core/featureFlags';
import { addError } from '@shared/utils/logger';
import { useCallback } from 'react';
import { useUserConsentForNearbyHotelsQuery } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';

interface WebsiteInterceptExternalHotelLinksPayload {
  regexList: string[];
  shouldNotContainList: string[];
}

const isWebsiteInterceptExternalHotelLinksPayloadValid = (payload: WebsiteInterceptExternalHotelLinksPayload) => {
  return payload && 'regexList' in payload && 'shouldNotContainList' in payload;
};

const parseRegexString = (regexString: string): RegExp => {
  if (!regexString.startsWith('/')) {
    addError('regexParseError', { type: 'invalid_format', regexString });
    throw new Error(`Invalid regex format: ${regexString}`);
  }

  const lastSlashIndex = regexString.lastIndexOf('/');
  if (lastSlashIndex <= 1) {
    addError('regexParseError', { type: 'invalid_format', regexString });
    throw new Error(`Invalid regex format: ${regexString}`);
  }

  const pattern = regexString.slice(1, lastSlashIndex);
  const flags = regexString.slice(lastSlashIndex + 1);

  try {
    return new RegExp(pattern, flags);
  } catch (e) {
    if (e instanceof Error) {
      addError('regexParseError', { type: 'parse_error', regexString, error: e });
      throw new Error(`Failed to parse regex: ${regexString} → ${e.message}`);
    } else {
      addError('regexParseError', { type: 'unknown_error', regexString, error: e });
      throw new Error(`Unknown error while parsing regex: ${regexString}`);
    }
  }
};

export const useWebsiteInterceptExternalHotelLinks = (isGuestView: boolean) => {
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;
  const { loading: isInterceptKeyQueryLoading, data: interceptKeyData, error: hasInterceptKeyQueryError } = useUserConsentForNearbyHotelsQuery({
    variables: {
      consentKey: 'websiteAllowHotelLinkIntercept',
      eventId: eventId!
    },
    skip: !eventId,
    batchMode: 'fast'
  });
  const { value, payload } = useFeatureValue(isGuestView ? 'websiteInterceptExternalHotelLinks' : 'websiteInterceptExternalHotelLinksAdmin');
  const websiteInterceptExternalHotelLinksPayload = payload as WebsiteInterceptExternalHotelLinksPayload;
  const skip = interceptKeyData?.userConsentKeyForEvent?.consentAnswer === false || !!hasInterceptKeyQueryError;

  const getShowIntercept = useCallback(
    (link: string = '') => {
      if (isWebsiteInterceptExternalHotelLinksPayloadValid(websiteInterceptExternalHotelLinksPayload)) {
        return (
          value === 'on' &&
          !skip &&
          !websiteInterceptExternalHotelLinksPayload.shouldNotContainList.some(substring => link.includes(substring)) &&
          websiteInterceptExternalHotelLinksPayload.regexList.some(regex => {
            try {
              const regexObj = parseRegexString(regex);
              return regexObj.test(link);
            } catch (error) {
              return false;
            }
          })
        );
      }
      return false;
    },
    [skip, value, websiteInterceptExternalHotelLinksPayload]
  );

  return { getShowIntercept, isInterceptKeyQueryLoading };
};
